import { Action } from 'redux';
import { put, select } from 'redux-saga/effects';

import { ACTION_CREATOR_NAME, paymentHelper, analyticsHelper, errorToBasketErrorModel } from 'helpers';
import { PaymentActionTypes, PaymentActions } from 'store/payment';
import { ErrorAction, AppState } from 'store';
import { api } from 'repositories/api';
import { PAYPAL_DECLINED_ERROR_CODE } from 'repositories/hybris.repository';
import { BasketActionTypes } from '../../../basket';
import { loadBasketSaga, validateBasketReservationsSaga } from '../../../basket/actions';
import { BasketErrorModel, BasketPaymentErrorModel } from 'models/Errors';
import { BasketSagaReturnType, callBasketSaga } from 'store/sagaHelpers';

const RESERVATION_ERROR_MESSAGE = 'Reservation has expired (pay by PayPal)';

export interface PaypalCompletePaymentAction extends Action<PaymentActionTypes.PAYPAL_COMPLETE_PAYMENT> {
}

export interface PaypalCompletePaymentActionStart extends Action<PaymentActionTypes.PAYPAL_COMPLETE_PAYMENT_START> {
}

export interface PaypalCompletePaymentActionSuccess extends Action<PaymentActionTypes.PAYPAL_COMPLETE_PAYMENT_SUCCESS> {
}

export interface PaypalCompletePaymentActionError extends ErrorAction<PaymentActionTypes.PAYPAL_COMPLETE_PAYMENT_ERROR> {
}

export const paypalCompletePayment: () => PaypalCompletePaymentAction = () => ({
    type: PaymentActionTypes.PAYPAL_COMPLETE_PAYMENT
});
paypalCompletePayment[ACTION_CREATOR_NAME] = 'paypalCompletePayment';

export type PaypalCompletePaymentFunc = (...args: Parameters<typeof paypalCompletePayment>) => void;

export function* paypalCompletePaymentSaga(): BasketSagaReturnType {
    yield put<PaymentActions>({
        type: PaymentActionTypes.PAYPAL_COMPLETE_PAYMENT_START
    });

    try {
        // we have to reload basket again, because something can be changed after payment page initialized
        // e.g.: customer opened one more tab with catalog, added basket item and then switched to payment page and tried to pay
        yield callBasketSaga(loadBasketSaga);
        yield callBasketSaga(validateBasketReservationsSaga, {
            type: BasketActionTypes.VALIDATE_BASKET_RESERVATIONS,
            isBasketPage: false
        });

        const state: AppState = yield select();

        if (state.basketState.shouldShowReservationWarning) {
            throw new BasketErrorModel(RESERVATION_ERROR_MESSAGE, undefined, 'paypalCompletePaymentSaga');
        }

        const paypalAuthResult = state.paymentState.paypalAuthSuccessfullResult!;

        yield sendAnalytics('takePayment');
        const paymentResult = yield api.hybris.executePaypalPayment(paypalAuthResult.paymentId, paypalAuthResult.payerId);
        yield sendAnalytics('paypalPaymentTaken');

        paymentHelper.saveSuccessfulPaymentResult(paymentResult);

        yield put<PaymentActions>({
            type: PaymentActionTypes.PAYPAL_COMPLETE_PAYMENT_SUCCESS
        });

        yield put<PaymentActions>({
            type: PaymentActionTypes.PAYPAL_PAYMENT_SUCCESS,
            paypalPaymentResult: paymentResult
        });

        return true;
    } catch (error) {
        if (error instanceof BasketPaymentErrorModel && error.code === PAYPAL_DECLINED_ERROR_CODE) {
            yield sendAnalytics('paypalPaymentDeclined');
        } else {
            yield sendAnalytics('errorTakingPaypalPayment');
        }

        yield put<PaymentActions>({
            type: PaymentActionTypes.PAYPAL_COMPLETE_PAYMENT_ERROR,
            error: errorToBasketErrorModel(error, 'paypalCompletePaymentSaga')
        });

        yield put<PaymentActions>({
            type: PaymentActionTypes.PAYPAL_PAYMENT_ERROR,
            error: errorToBasketErrorModel(error, 'paypalCompletePaymentSaga')
        });

        return false;
    }
}

function* sendAnalytics(status: 'errorTakingPaypalPayment' | 'takePayment' | 'paypalPaymentTaken' | 'paypalPaymentDeclined'): Generator<any, void, any> {
    yield analyticsHelper.withLoggingGenerator(function* () {
        const state: AppState = yield select();
        analyticsHelper.sendToGoogleMessageAboutPaymentError(state.basketState.basket, state.paymentState.paymentDetails);
        analyticsHelper.sendToGooglePaypalPaymentStatus(status);
    }, 'paypalCompletePaymentSaga');
}
